/**
 * Taktwaschanlagen — die erste Auswertungs-Seite im Leitstand-Stil.
 *
 * Setzt den Entwurf vom 13.08.2026 um: Kennzahlen-Leiste, „Warum stand die
 * Anlage" (Störmeldungen der Steuerung, bislang unausgewertet), Zeitaufteilung
 * je Anlage, Schichttabelle, Waschmittel und Programme. Statt raumgreifender
 * Diagramme kompakte Ranglisten mit schmaler Balkenspur.
 *
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTodayBerlin } from '@wash-ops/shared';
import { useAuth } from '../App';
import { LeitstandRahmen } from '../components/leitstand/LeitstandRahmen';
import {
  Anteilsliste,
  Karte,
  Kennung,
  Kennzahl,
  Kopfzelle,
  Marke,
  Notiz,
  Rangliste,
  Tabelle,
  Zeitbalken,
  Zelle,
} from '../components/leitstand';

/* ── Datentypen (Spiegel von GET /api/reports/twa/leitstand) ───────────── */

interface Schicht {
  shiftDate: string;
  shiftType: string;
  kg: number;
  takte: number;
  laufzeitH: number;
  stoerzeitH: number;
  standzeitH: number;
  isOngoing: boolean;
}

interface Stoerung {
  meldungsNr: number;
  text: string;
  dauerH: number;
  anzahl: number;
}

interface Maschine {
  code: string;
  name: string;
  kg: number;
  takte: number;
  kgProTakt: number | null;
  laufzeitH: number;
  stoerzeitH: number;
  standzeitH: number;
  anteilLaufProzent: number | null;
  anteilStoerProzent: number | null;
  anteilStandProzent: number | null;
  schichten: Schicht[];
  stoerungen: Stoerung[];
  waschmittel: Array<{ name: string; menge: number }>;
  programme: Array<{ programmNr: number; gewichtKg: number; anteilProzent: number | null }>;
}

interface LeitstandDaten {
  gesamt: {
    kg: number;
    takte: number;
    kgProTakt: number | null;
    nutzungsgradProzent: number | null;
    stoerzeitProzent: number | null;
    stoerzeitH: number;
    veraenderungProzent: number | null;
  };
  maschinen: Maschine[];
  ungeklaerte: Ungeklaert[];
  hasOngoingShift: boolean;
}

/** Meldungen der Anlage, die zu keiner Schicht gehoeren — samt Grund. */
interface Ungeklaert {
  grund: string;
  anzahl: number;
  vonTag: string | null;
  bisTag: string | null;
}

/** 2026-07-07 → 07.07.2026 */
function tagKurz(iso: string): string {
  return iso.split('-').reverse().join('.');
}

/* ── Zeitraum-Presets, gerechnet auf Berliner Kalendertagen ────────────── */

type Preset = 'heute' | 'woche' | 'monat' | 'quartal' | 'frei';

function tagPlus(iso: string, delta: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

function zeitraumFuer(preset: Preset): { von: string; bis: string } {
  const heute = getTodayBerlin(new Date());
  if (preset === 'heute') return { von: heute, bis: heute };
  if (preset === 'woche') {
    const wochentag = new Date(`${heute}T00:00:00Z`).getUTCDay(); // 0=So
    const seitMontag = (wochentag + 6) % 7;
    return { von: tagPlus(heute, -seitMontag), bis: heute };
  }
  if (preset === 'monat') return { von: `${heute.slice(0, 8)}01`, bis: heute };
  return { von: tagPlus(heute, -89), bis: heute };
}

const PRESETS: Array<{ schluessel: Preset; label: string }> = [
  { schluessel: 'heute', label: 'Heute' },
  { schluessel: 'woche', label: 'Diese Woche' },
  { schluessel: 'monat', label: 'Dieser Monat' },
  { schluessel: 'quartal', label: '90 Tage' },
  { schluessel: 'frei', label: 'Zeitraum' },
];

/* ── Formatierung ──────────────────────────────────────────────────────── */

const WOCHENTAGE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

function fmt(wert: number, nachkomma = 0): string {
  return wert.toLocaleString('de-DE', {
    minimumFractionDigits: nachkomma,
    maximumFractionDigits: nachkomma,
  });
}

function fmtTag(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${WOCHENTAGE[d.getUTCDay()]} ${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
}

const SCHICHT_LABEL: Record<string, string> = {
  EARLY: 'FRÜH',
  LATE: 'SPÄT',
  SATURDAY: 'SAMSTAG',
};

/* ── Kleine Umschalt-Knopfgruppe im Leitstand-Ton ──────────────────────── */

function Umschalter<T extends string>({
  wert,
  optionen,
  onWechsel,
}: {
  wert: T;
  optionen: Array<{ schluessel: T; label: string }>;
  onWechsel: (neu: T) => void;
}) {
  return (
    <div className="flex gap-1.5">
      {optionen.map((o) => (
        <button
          key={o.schluessel}
          onClick={() => onWechsel(o.schluessel)}
          aria-pressed={wert === o.schluessel}
          className={`rounded-[4px] border px-[11px] py-1.5 font-zahl text-xs transition-colors ${
            wert === o.schluessel
              ? 'border-akzent bg-akzent text-akzent-satz'
              : 'border-linie-2 bg-flaeche text-tinte-2 hover:border-linie-stark'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ── Die Seite ─────────────────────────────────────────────────────────── */

export default function TwaLeitstand() {
  const { user } = useAuth();

  const [preset, setPreset] = useState<Preset>('woche');
  const [freiVon, setFreiVon] = useState('');
  const [freiBis, setFreiBis] = useState('');
  const [maschinenCode, setMaschinenCode] = useState('TWA_P50');
  const [stoerSort, setStoerSort] = useState<'zeit' | 'haeufig'>('zeit');

  const [daten, setDaten] = useState<LeitstandDaten | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  const zeitraum = useMemo(() => {
    if (preset === 'frei') {
      return freiVon && freiBis ? { von: freiVon, bis: freiBis } : null;
    }
    return zeitraumFuer(preset);
  }, [preset, freiVon, freiBis]);

  const lade = useCallback(async () => {
    if (!zeitraum) return;
    setLaden(true);
    setFehler('');
    try {
      const res = await fetch(
        `/api/reports/twa/leitstand?from=${zeitraum.von}&to=${zeitraum.bis}`,
        { credentials: 'include' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Die Auswertung konnte nicht geladen werden');
      }
      const body = await res.json();
      if (!body || !Array.isArray(body.maschinen) || !body.gesamt) {
        throw new Error('Unerwartete Antwort vom Server — läuft die aktuelle API-Version?');
      }
      setDaten(body);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Auswertung konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, [zeitraum]);

  useEffect(() => {
    void lade();
  }, [lade]);

  const maschine = daten?.maschinen.find((m) => m.code === maschinenCode) ?? null;

  const stoerungen = useMemo(() => {
    if (!maschine) return [];
    const sortiert = [...maschine.stoerungen].sort((a, b) =>
      stoerSort === 'zeit' ? b.dauerH - a.dauerH : b.anzahl - a.anzahl,
    );
    const top = sortiert.slice(0, 8);
    const spitze = stoerSort === 'zeit' ? top[0]?.dauerH : top[0]?.anzahl;
    return top.map((s) => ({
      nr: String(s.meldungsNr).padStart(3, '0'),
      text: s.text,
      wert: stoerSort === 'zeit' ? `${fmt(s.dauerH, 1)} h` : `${fmt(s.anzahl)} ×`,
      neben: stoerSort === 'zeit' ? `${fmt(s.anzahl)} ×` : `${fmt(s.dauerH, 1)} h`,
      anteil: spitze
        ? Math.max(2, ((stoerSort === 'zeit' ? s.dauerH : s.anzahl) / spitze) * 100)
        : 0,
    }));
  }, [maschine, stoerSort]);

  const waschmittel = useMemo(() => {
    if (!maschine) return [];
    const spitze = maschine.waschmittel[0]?.menge ?? 0;
    return maschine.waschmittel.slice(0, 10).map((w) => ({
      name: w.name,
      wert: fmt(w.menge),
      anteil: spitze > 0 ? Math.max(2, (w.menge / spitze) * 100) : 0,
    }));
  }, [maschine]);

  const programme = useMemo(() => {
    if (!maschine) return [];
    const top = maschine.programme.slice(0, 8);
    const rest = maschine.programme.slice(8);
    const spitze = top[0]?.anteilProzent ?? 0;
    const zeilen = top.map((p) => ({
      name: `Programm ${p.programmNr}`,
      wert: p.anteilProzent === null ? '—' : `${fmt(p.anteilProzent, 1)} %`,
      anteil:
        spitze > 0 && p.anteilProzent !== null ? Math.max(2, (p.anteilProzent / spitze) * 100) : 0,
    }));
    const restAnteil = rest.reduce((s, p) => s + (p.anteilProzent ?? 0), 0);
    if (rest.length > 0 && restAnteil > 0) {
      zeilen.push({
        name: `übrige (${rest.length})`,
        wert: `${fmt(restAnteil, 1)} %`,
        anteil: spitze > 0 ? Math.max(2, (restAnteil / spitze) * 100) : 0,
      });
    }
    return zeilen;
  }, [maschine]);

  const zeitraumMarke = zeitraum ? `${fmtTag(zeitraum.von)} – ${fmtTag(zeitraum.bis)}` : '';

  return (
    <LeitstandRahmen
      titel="Taktwaschanlagen"
      benutzer={user?.displayName}
      werkzeuge={
        <div className="flex flex-wrap items-center gap-2">
          <Umschalter wert={preset} optionen={PRESETS} onWechsel={setPreset} />
          {preset === 'frei' && (
            <span className="flex items-center gap-1.5">
              <input
                type="date"
                value={freiVon}
                onChange={(e) => setFreiVon(e.target.value)}
                className="rounded-[4px] border border-linie-2 bg-flaeche px-2 py-1 font-zahl text-xs text-tinte"
              />
              <span className="text-xs text-tinte-3">bis</span>
              <input
                type="date"
                value={freiBis}
                onChange={(e) => setFreiBis(e.target.value)}
                className="rounded-[4px] border border-linie-2 bg-flaeche px-2 py-1 font-zahl text-xs text-tinte"
              />
            </span>
          )}
        </div>
      }
    >
      {fehler && (
        <Notiz titel="Konnte nicht laden">
          {fehler}{' '}
          <button onClick={() => void lade()} className="underline">
            Erneut versuchen
          </button>
        </Notiz>
      )}

      {laden && !daten && (
        <p className="py-12 text-center text-sm text-tinte-3">Lade Auswertung …</p>
      )}

      {daten && (
        <div className={`flex flex-col gap-6 ${laden ? 'opacity-60' : ''}`}>
          {/* Kennzahlen über beide Anlagen */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kennzahl
              marke="Gewaschen"
              wert={fmt(daten.gesamt.kg / 1000, 2)}
              einheit="t"
              neben={
                daten.gesamt.veraenderungProzent === null
                  ? undefined
                  : `${daten.gesamt.veraenderungProzent > 0 ? '+' : ''}${fmt(daten.gesamt.veraenderungProzent, 1)} % zum Zeitraum davor`
              }
              tendenz={
                daten.gesamt.veraenderungProzent === null
                  ? 'neutral'
                  : daten.gesamt.veraenderungProzent >= 0
                    ? 'hoch'
                    : 'runter'
              }
            />
            <Kennzahl
              marke="Takte"
              wert={fmt(daten.gesamt.takte)}
              neben={
                daten.gesamt.kgProTakt === null
                  ? undefined
                  : `${fmt(daten.gesamt.kgProTakt, 1)} kg je Takt`
              }
            />
            <Kennzahl
              marke="Nutzungsgrad"
              wert={
                daten.gesamt.nutzungsgradProzent === null
                  ? '—'
                  : fmt(daten.gesamt.nutzungsgradProzent, 1)
              }
              einheit={daten.gesamt.nutzungsgradProzent === null ? undefined : '%'}
              neben="Laufzeit an der Gesamtzeit"
            />
            <Kennzahl
              marke="Störzeit"
              wert={
                daten.gesamt.stoerzeitProzent === null ? '—' : fmt(daten.gesamt.stoerzeitProzent, 1)
              }
              einheit={daten.gesamt.stoerzeitProzent === null ? undefined : '%'}
              neben={`${fmt(daten.gesamt.stoerzeitH, 1)} h im Zeitraum`}
              tendenz={
                daten.gesamt.stoerzeitProzent !== null && daten.gesamt.stoerzeitProzent > 10
                  ? 'runter'
                  : 'neutral'
              }
            />
          </div>

          {/* Anlagenwahl für alles Maschinenbezogene darunter */}
          <div className="flex items-center gap-3">
            <Marke>Anlage</Marke>
            <Umschalter
              wert={maschinenCode}
              optionen={daten.maschinen.map((m) => ({ schluessel: m.code, label: m.name }))}
              onWechsel={setMaschinenCode}
            />
          </div>

          <Karte
            titel="Warum stand die Anlage"
            marke={`${maschine?.name ?? ''} · ${zeitraumMarke}`}
          >
            <div className="mb-4">
              <Umschalter
                wert={stoerSort}
                optionen={[
                  { schluessel: 'zeit', label: 'Nach Ausfallzeit' },
                  { schluessel: 'haeufig', label: 'Nach Häufigkeit' },
                ]}
                onWechsel={setStoerSort}
              />
            </div>
            {stoerungen.length === 0 ? (
              <p className="py-4 text-sm text-tinte-3">Keine Störmeldungen im Zeitraum.</p>
            ) : (
              <Rangliste eintraege={stoerungen} />
            )}
          </Karte>

          <div className="grid gap-5 lg:grid-cols-2">
            {daten.maschinen.map((m) => (
              <Karte key={m.code} titel="Zeitaufteilung" marke={m.name}>
                {m.anteilLaufProzent === null ? (
                  <p className="py-4 text-sm text-tinte-3">Keine Zeitmessung im Zeitraum.</p>
                ) : (
                  <Zeitbalken
                    abschnitte={[
                      {
                        anteil: m.anteilLaufProzent,
                        farbe: 'lauf',
                        name: 'Laufzeit',
                        wert: `${fmt(m.laufzeitH, 1)} h`,
                      },
                      {
                        anteil: m.anteilStoerProzent ?? 0,
                        farbe: 'stoer',
                        name: 'Störzeit',
                        wert: `${fmt(m.stoerzeitH, 1)} h`,
                      },
                      {
                        anteil: m.anteilStandProzent ?? 0,
                        farbe: 'stand',
                        name: 'Standzeit',
                        wert: `${fmt(m.standzeitH, 1)} h`,
                      },
                    ]}
                  />
                )}
              </Karte>
            ))}
          </div>

          <Karte titel="Schichten" marke={`${maschine?.name ?? ''} · ${zeitraumMarke}`} tabelle>
            {maschine && maschine.schichten.length > 0 ? (
              <Tabelle>
                <thead>
                  <tr>
                    <Kopfzelle>Tag</Kopfzelle>
                    <Kopfzelle>Schicht</Kopfzelle>
                    <Kopfzelle rechts>Gewicht</Kopfzelle>
                    <Kopfzelle rechts>Takte</Kopfzelle>
                    <Kopfzelle rechts>kg/Takt</Kopfzelle>
                    <Kopfzelle rechts>Störzeit</Kopfzelle>
                    <Kopfzelle rechts>Nutzung</Kopfzelle>
                  </tr>
                </thead>
                <tbody>
                  {maschine.schichten.slice(0, 20).map((s, i) => {
                    const gezeigt = Math.min(maschine.schichten.length, 20);
                    const zeitBasis = s.laufzeitH + s.stoerzeitH + s.standzeitH;
                    const letzte = i === gezeigt - 1;
                    return (
                      <tr key={`${s.shiftDate}-${s.shiftType}`}>
                        <Zelle letzte={letzte}>{fmtTag(s.shiftDate)}</Zelle>
                        <Zelle letzte={letzte}>
                          <span className="inline-flex items-center gap-2">
                            <Kennung>{SCHICHT_LABEL[s.shiftType] ?? s.shiftType}</Kennung>
                            {s.isOngoing && (
                              <span className="font-zahl text-[10px] text-lauf">läuft</span>
                            )}
                          </span>
                        </Zelle>
                        <Zelle rechts zahl letzte={letzte}>
                          {fmt(s.kg)} kg
                        </Zelle>
                        <Zelle rechts zahl letzte={letzte}>
                          {fmt(s.takte)}
                        </Zelle>
                        <Zelle rechts zahl letzte={letzte}>
                          {s.takte > 0 ? fmt(s.kg / s.takte, 1) : '—'}
                        </Zelle>
                        <Zelle rechts zahl letzte={letzte}>
                          {zeitBasis > 0 ? `${fmt(s.stoerzeitH, 1)} h` : '—'}
                        </Zelle>
                        <Zelle rechts zahl letzte={letzte}>
                          {zeitBasis > 0 ? `${fmt((s.laufzeitH / zeitBasis) * 100)} %` : '—'}
                        </Zelle>
                      </tr>
                    );
                  })}
                </tbody>
              </Tabelle>
            ) : (
              <p className="py-4 text-sm text-tinte-3">Keine Schichtdaten im Zeitraum.</p>
            )}
            {maschine && maschine.schichten.length > 20 && (
              <p className="pt-3 text-xs text-tinte-3">
                Letzte 20 von {maschine.schichten.length} Schichten — für die vollständige Liste den
                Zeitraum verkürzen.
              </p>
            )}
          </Karte>

          <div className="grid gap-5 lg:grid-cols-2">
            <Karte titel="Waschmittel" marke={`${maschine?.name ?? ''} · Angabe der Steuerung`}>
              {waschmittel.length === 0 ? (
                <p className="py-4 text-sm text-tinte-3">Kein Verbrauch im Zeitraum.</p>
              ) : (
                <div className="flex flex-col gap-4">
                  <Anteilsliste eintraege={waschmittel} />
                  <p className="text-xs text-tinte-3">
                    Die Steuerung liefert die Verbräuche ohne Einheit (flüssige Mittel); die Einheit
                    wird mit ELIS geklärt.
                  </p>
                </div>
              )}
            </Karte>

            <Karte titel="Programme" marke={`${maschine?.name ?? ''} · Anteil am Gewicht`}>
              {programme.length === 0 ? (
                <p className="py-4 text-sm text-tinte-3">Keine Programmdaten im Zeitraum.</p>
              ) : (
                <Anteilsliste eintraege={programme} />
              )}
            </Karte>
          </div>

          {daten.ungeklaerte.length > 0 && (
            <Karte
              titel="Ohne Schichtzuordnung"
              marke={`${daten.ungeklaerte.reduce((s, u) => s + u.anzahl, 0).toLocaleString('de-DE')} Meldungen der Anlage`}
            >
              <p className="pb-3 text-[13px] text-tinte-2">
                Diese Meldungen gehören zu keiner Schicht und zählen deshalb in keiner Auswertung
                mit. Sie stehen hier, statt still einer falschen Schicht zugeschlagen zu werden.
              </p>
              <Tabelle>
                <thead>
                  <tr>
                    <Kopfzelle>Grund</Kopfzelle>
                    <Kopfzelle rechts>Meldungen</Kopfzelle>
                    <Kopfzelle>Zeitraum laut Anlage</Kopfzelle>
                  </tr>
                </thead>
                <tbody>
                  {daten.ungeklaerte.map((u) => (
                    <tr key={u.grund}>
                      <Zelle>{u.grund}</Zelle>
                      <Zelle rechts zahl>
                        {u.anzahl.toLocaleString('de-DE')}
                      </Zelle>
                      <Zelle>
                        {u.vonTag && u.bisTag ? `${tagKurz(u.vonTag)} – ${tagKurz(u.bisTag)}` : '—'}
                      </Zelle>
                    </tr>
                  ))}
                </tbody>
              </Tabelle>
            </Karte>
          )}

          <p className="border-t border-linie pt-4 text-xs text-tinte-3">
            Störmeldungen und Verbräuche stammen aus der Anlagensteuerung.
          </p>
        </div>
      )}
    </LeitstandRahmen>
  );
}
